import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

//Styled Components
import {
  Container,
  ButtonContainer,
  PaginationButtonsStyled,
  MainButtonStyled,
  CloseButton,
  EmptyMessage,
  BelowTable,
} from './styles';

// Types
import { ModalProps } from './types';
import {
  dataTable,
  imperativeRefPagination,
} from '../../../display/tables/InputRadioDefaultTable/types';
import {
  tableIds,
  takeBase,
  takeModalSurveillanceRoute,
} from '../../../display/tables/types';
import { dataTypes as DataTypesGenericOrderModal } from '../../../display/tables/InputRadioDefaultTable/types';
import { dataTypes } from '../../../display/tables/CampaignTable/types';
import { tableProps as InputRadioTagTableProps } from '../../../display/tables/InputRadioTagTable/types';
import { SurveillanceRoute } from '../../../../types/surveillanceRoute';

// Components
import ModalContainer from '../../containers/ModalContainer';
import InputRadioDefaultTable from '../../../display/tables/InputRadioDefaultTable';
import { InputRadioTagTable } from '../../../display/tables/InputRadioTagTable';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from '../../../../state/actions/ui/modal';
import { RelatedCampaignTable } from '../../../display/tables/RelatedCampaignTable';

// GraphQL
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { FETCH_TEAMS } from '../../../../gql/queries/users';
import {
  GET_CONCESSIONS,
  GET_ORDER_BY_ID,
} from '../../../../gql/queries/orders/orders';
import { GET_GENERIC_ORDERS_MODAL } from '../../../../gql/queries/genericOrders';
import { GET_CAMPAIGNS_MODAL } from '../../../../gql/queries/campaign';
import { GET_SURVEILLANCE_ROUTES_MODAL } from '../../../../gql/queries/surveillanceRoute';

// Selectors
import {
  selectedRowModalAcceptData,
  selectedRowModalTable,
} from '../../../../state/actions/ui/selectRowTableModal';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { selectOrdersForSurveillanceRoutes } from '../../../../gql/selectors/surveillanceRoute';
import { adaptSurveillanceRouteToOrder } from '../../../../utils/surveillanceRoute';
import {
  SR_HIDDEN_COLUMN_IDS,
  SR_HIDDEN_FILTER_IDS,
} from '../../../../pages/main/surveillanceRoute/constants';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { campaignIdTypes } from '../../../../pages/main/workOrders/Campaigns/types';
import * as Sentry from '@sentry/react';
import { showErrorToast } from '../../../../utils/toasts';
import { isLegacyNetworkFailure } from '../../../../offline/transportFailure';
import Tabs from '../../../display/Tabs';
import { getCurrentRowDataModal } from '../../../../state/selectors/ui/modal';
import { client } from '../../../../gql/client';

// Cache helpers
import {
  evictAfterSegmentEdit,
  evictDerivedCaches,
} from '../../../../gql/cacheHelpers';
import {
  CONVERT_ACTION,
  TRANSFER_ACCIDENT,
  TRANSFER_ACTION,
  TRANSFER_COMMUNICATION,
} from '../../../../gql/mutations/orders';
import { modalIds } from '../../SuperModal/types';
import { OrderState, OrderType } from '../../../../types/orders';
import { rvTransferTargetStates } from '../../../../utils/rvTransferTargets';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { FETCH_ACTIONS } from '../../../../gql/queries/actions/actions';
import { FETCH_COMMUNICATIONS } from '../../../../gql/queries/communication';
import { FETCH_ACCIDENTS } from '../../../../gql/queries/accidents/accidents';
import { store } from '../../../../state/configureStore';
import { useAuth } from '../../../../hooks/useAuth';
import { useRoadNamesById } from '../../../../hooks/useRoadDirectionLabels';
import { roadIdsInvolved } from '../../../../utils/roadStretches';
import { liveLinkedBasicActionOrder } from '../../../../utils/segmentLinkedBasicAction';
import { GET_ROUTE_SEGMENTS } from '../../../../gql/queries/surveillanceRoute';
import { RouteSegment } from '../../../../types/surveillanceRoute';
import SegmentList from '../../../../pages/main/surveillanceRoute/components/SegmentList';
import {
  canConvertAction,
  convertModeFor,
} from '../../../../utils/convertAction';
import {
  showBackendErrorToast,
  showSuccessToast,
} from '../../../../utils/toasts';
// «Convertir actuación» (W4): avisos, frase y opciones con las piezas que ya
// existen («Corregir tramo» y las opciones del informe del recorrido).
import {
  CrossingNotice as Notice,
  CrossingNoticeIcon as NoticeIcon,
  InlineError,
  Text,
} from '../EditSegmentModal/styles';
import {
  ModeRow,
  OptionText,
} from '../DownloadHistoricModal/SurveillanceReportOptions/styles';

// ═══ Modo RV — transferir una actuación básica SOLO a otro RV (2026-07-21) ═══
// Elegibilidad de destino (decisión de producto): RVs OPERATIVOS, nunca
// end/annulled. Desde transferir tramo (2026-10-07) gestión ve también los
// pendientes de validar: la básica sigue al tramo que se transfirió; la tablet
// no. La lista vive en `utils/rvTransferTargets` (con su unit). El filtro
// viaja FORZADO en la query (el filtro Estado de la tabla queda oculto).

// Columnas/filtros del dominio RV en el modal: los mismos recortes del
// listado RV (constantes compartidas) más los que en contexto modal no
// tienen dataset u opción de usuario:
// - 'road': el modal no carga `roads` en filtersDataOptions (el filtro
//   Carretera ya se auto-oculta sin opciones; la columna mostraría vacío).
// - 'state' (filtro): los estados destino van forzados a operativos.
// - 'creator' (filtro): sin dataset `users` en el modal (se auto-oculta,
//   explícito por claridad).
const RV_TRANSFER_MODAL_HIDDEN_COLUMN_IDS = [...SR_HIDDEN_COLUMN_IDS, 'road'];
const RV_TRANSFER_MODAL_HIDDEN_FILTER_IDS = [
  ...SR_HIDDEN_FILTER_IDS,
  'state',
  'creator',
];

type TabKind = 'generic' | 'campaign' | 'rv';

export const TransferSubOrderModal = ({
  onClickClose,
  namingPersistForm,
  orderToTransfer,
  currentParentOrder,
  revalidatorFunction,
  ...props
}: ModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const roleActive = user?.user?.rolActive;
  const [genericOrdersData, setGenericOrdersData] = useState<dataTable[]>([]);
  const [campaignData, setCampaignData] = useState<dataTable[]>([]);
  const [campaignRowState, setCampaignRowState] = useState<campaignIdTypes>({});
  // True cuando el usuario ha seleccionado una fila en la tabla de OT genéricas.
  // Inicia en false (no hay nada seleccionado al abrir el modal). El callback que
  // pasamos a InputRadioDefaultTable (setIsSelectedRow) lo pone a true al seleccionar
  // y a false al deseleccionar. Antes este flag estaba invertido (init=true) y la
  // condición `disabled={genericOrderRowState}` mantenía el botón siempre deshabilitado.
  const [hasSelectedGenericOrderRow, setHasSelectedGenericOrderRow] =
    useState<boolean>(false);

  // ═══ Modo RV (F1, 2026-07-21) ═══
  // Detección por el type REAL de la Order gemela: desde que el selector dejó
  // de enmascarar el RV como `generic` (d7ca4e0d), los 3 openers del modal
  // (ActionOT/AccidentOT/CommunicationOT) construyen `currentParentOrder`
  // desde data[0], que en el detalle RV llega con type='surveillanceRoute'.
  // Sin prop nuevo. En la práctica solo ActionOT abre este modal en contexto
  // RV (el RV solo admite Actuaciones como hijas — hierarchy-validator).
  // Ausente ⇒ comportamiento actual intacto (2 tabs OT+Campañas).
  const isRvMode = currentParentOrder?.type === OrderType.SurveillanceRoute;
  // Filas de la tabla RV, ya enriquecidas con la Order gemela (contrato
  // espejo de selectAllOrders: fila.id = Order gemela → newParentOrderId;
  // fila.genericOrCampaignOrderID = id de la ENTITY SurveillanceRoute).
  const [rvRows, setRvRows] = useState<dataTable[]>([]);
  // Selección local string (contrato setRowState de InputRadioTagTable) —
  // contiene el id de la ORDER gemela del RV destino; un efecto la sincroniza
  // a selectedRowModalTable (patrón tab Campañas) para que acceptData no
  // cambie.
  const [selectedRvOrderId, setSelectedRvOrderId] = useState('');
  const [isResolvingRvOrders, setIsResolvingRvOrders] = useState(false);

  const [allData, setAllData] = useState<any>();
  const [tabIndex, setTabIndex] = useState(0);

  // ═══ «Convertir actuación» (W4, 15-oct) ═══
  // Gestión ve además el destino del OTRO mundo: una básica, las OT genéricas
  // y las campañas; una actuación de OT, los recorridos. Transferir (el mismo
  // mundo) sigue siendo la primera pestaña y no cambia para nadie.
  //
  // Con el recorrido de la básica EN CURSO no se ofrece (revisión W4, M-8): la
  // tablet puede tener cambios de esa básica encolados y el servidor lo niega
  // (ORDERS_CONVERT_ROUTE_IN_PROGRESS).
  const canConvert =
    orderToTransfer?.type === OrderType.Action &&
    canConvertAction(
      roleActive,
      orderToTransfer?.state,
      orderToTransfer?.isValidate
    ) &&
    !(isRvMode && orderToTransfer?.parentState === OrderState.InProgress);
  const tabKinds: TabKind[] = isRvMode
    ? canConvert
      ? ['rv', 'generic', 'campaign']
      : ['rv']
    : canConvert
      ? ['generic', 'campaign', 'rv']
      : ['generic', 'campaign'];
  const activeTab: TabKind = tabKinds[tabIndex] ?? tabKinds[0];
  const isCampaignTab = activeTab === 'campaign';
  const showsRvTable = tabKinds.includes('rv');
  const showsOtTables = tabKinds.includes('generic');
  // El destino de la pestaña activa es del otro mundo: se convierte.
  const isConvertTab =
    canConvert && (isRvMode ? activeTab !== 'rv' : activeTab === 'rv');
  // Doble registro: la OT ya tiene el trabajo y solo se anula la básica.
  const [annulOnly, setAnnulOnly] = useState(false);
  // Actuación de OT → básica: los tramos del recorrido elegido y el marcado.
  const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  // Por qué no vale el tramo que se ha tocado: el código del rechazo que daría
  // el servidor (se pinta con su texto de `errors:backend`). Null = vale.
  const [segmentPickError, setSegmentPickError] = useState<string | null>(null);
  // Uno por apertura del modal: reintentar aquí tras un error no convierte dos
  // veces (si la primera llegó a aplicarse, el servidor devuelve lo mismo).
  const convertOpId = useMemo(() => crypto.randomUUID(), []);
  const [isLoading, setIsLoading] = useState(false);
  // Guard SÍNCRONO de doble-envío: el `disabled` del botón depende de estado
  // React (isLoading) que bajo red lenta se propaga tarde → un doble-clic
  // colaba un 2º makeTransfer. El ref es inmediato.
  const isSubmittingRef = useRef(false);
  const [currentServerPage, setCurrentServerPage] = useState(1);
  const skipTakeSyncRef = useRef(false);

  const selector = useSelector(
    getCurrentRowDataModal(namingPersistForm),
    isEqual
  );

  const selectorCursorGenericOrders = useSelector(
    getCursor(tableIds.GENERIC_ORDER_MODAL),
    isEqual
  );

  const selectorCursorCampaign = useSelector(
    getCursor(tableIds.CAMPAIGN_MODAL),
    isEqual
  );

  const selectorCursorRv = useSelector(
    getCursor(tableIds.SURVEILLANCE_ROUTE_MODAL),
    isEqual
  );

  const [pageSizeCampaigns, setPageSizeCampaigns] = useState(
    selectorCursorCampaign.take
  );

  const [pageSizeGenericsOrders, setPageSizeGenericsOrders] = useState(
    selectorCursorGenericOrders.take
  );

  // Reset a página 1 al abrir la modal
  useEffect(() => {
    setCurrentServerPage(1);
    if (showsRvTable) {
      // Modo RV: solo el cursor propio (patrón GENERIC_ORDER_MODAL — los
      // filtros persisten entre aperturas; cursor/take vuelven a página 1).
      setSelectedRvOrderId('');
      dispatch(
        setCursor(tableIds.SURVEILLANCE_ROUTE_MODAL as string, {
          ...selectorCursorRv,
          cursor: '',
          take: takeModalSurveillanceRoute,
        })
      );
    }
    if (!showsOtTables) return;
    dispatch(
      setCursor(tableIds.GENERIC_ORDER_MODAL as string, {
        ...selectorCursorGenericOrders,
        cursor: '',
        take: Math.abs(selectorCursorGenericOrders.take) || 8,
      })
    );
    dispatch(
      setCursor(tableIds.CAMPAIGN_MODAL as string, {
        ...selectorCursorCampaign,
        cursor: '',
        take: Math.abs(selectorCursorCampaign.take) || 8,
      })
    );
  }, []); // Solo al montar

  // FIX A4: Pre-set concessionId si solo hay 1 concesión (evita partición de cache Apollo)
  const autoConcessionId = (() => {
    try {
      const cached = client.readQuery({ query: GET_CONCESSIONS });
      if (cached?.concessions?.length === 1) return cached.concessions[0].id;
    } catch {
      /* cache not available */
    }
    return undefined;
  })();
  const effectiveGenericConcessionId =
    selectorCursorGenericOrders.concessionId || autoConcessionId;
  const effectiveCampaignConcessionId =
    selectorCursorCampaign?.concessionId || autoConcessionId;

  useEffect(() => {
    if (!showsOtTables) return; // el pre-set de concesión es de las tabs OT/Campañas
    if (autoConcessionId) {
      if (!selectorCursorGenericOrders.concessionId) {
        dispatch(
          setCursor(tableIds.GENERIC_ORDER_MODAL as string, {
            ...selectorCursorGenericOrders,
            concessionId: autoConcessionId,
          })
        );
      }
      if (!selectorCursorCampaign?.concessionId) {
        dispatch(
          setCursor(tableIds.CAMPAIGN_MODAL as string, {
            ...selectorCursorCampaign,
            concessionId: autoConcessionId,
          })
        );
      }
    }
  }, [autoConcessionId]);

  useEffect(() => {
    if (skipTakeSyncRef.current) {
      skipTakeSyncRef.current = false;
      return;
    }
    setPageSizeCampaigns(Math.abs(selectorCursorCampaign.take));
    setPageSizeGenericsOrders(Math.abs(selectorCursorGenericOrders.take));
  }, [selectorCursorCampaign, selectorCursorGenericOrders]);

  // Reset page counter when filters change (cursor resets to '')
  useEffect(() => {
    if (selectorCursorGenericOrders.cursor === '') {
      setCurrentServerPage(1);
    }
  }, [selectorCursorGenericOrders.cursor]);

  useEffect(() => {
    if (selectorCursorCampaign?.cursor === '') {
      setCurrentServerPage(1);
    }
  }, [selectorCursorCampaign?.cursor]);

  const { error, loading, data } =
    isCampaignTab
      ? useQuery(GET_CAMPAIGNS_MODAL, {
          // En esta rama la pestaña activa es la de campañas.
          skip: !showsOtTables,
          fetchPolicy: 'cache-first',
          variables: {
            input: {
              cursor: selectorCursorCampaign?.cursor,
              take: selectorCursorCampaign?.take,
              orderBy: selectorCursorCampaign?.orderBy,
              concessionId: effectiveCampaignConcessionId,
              fromDate: selectorCursorCampaign?.fromDate,
              toDate: selectorCursorCampaign?.toDate,
              name: selectorCursorCampaign.name,
              state: selectorCursorCampaign.state,
              teamIds: selectorCursorCampaign?.teamIds ?? [],
              track: selectorCursorCampaign?.track,
              predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
              pkInitKm: selectorCursorCampaign?.pkInitKm,
              pkInitMeter: selectorCursorCampaign?.pkInitMeter,
              pkEndKm: selectorCursorCampaign?.pkEndKm,
              pkEndMeter: selectorCursorCampaign?.pkEndMeter,
            },
          },
        })
      : useQuery(GET_GENERIC_ORDERS_MODAL, {
          skip: !showsOtTables || activeTab === 'rv',
          fetchPolicy: 'cache-first',
          variables: {
            input: {
              take: selectorCursorGenericOrders.take,
              orderBy: selectorCursorGenericOrders.orderBy,
              cursor: selectorCursorGenericOrders.cursor,
              toDate: selectorCursorGenericOrders.toDate,
              fromDate: selectorCursorGenericOrders.fromDate,
              indexType: selectorCursorGenericOrders.indexType,
              indexExpedition: selectorCursorGenericOrders.indexExpedition,
              classification: selectorCursorGenericOrders.classification,
              capitolId: selectorCursorGenericOrders.capitolId,
              subCapitolId: selectorCursorGenericOrders.subCapitolId,
              concessionId: effectiveGenericConcessionId,
              pkInitKm: selectorCursorGenericOrders.pkInitKm,
              pkInitMeter: selectorCursorGenericOrders.pkInitMeter,
              pkEndKm: selectorCursorGenericOrders.pkEndKm,
              pkEndMeter: selectorCursorGenericOrders.pkEndMeter,
              teamIds: selectorCursorGenericOrders.teamIds ?? [],
              track: selectorCursorGenericOrders.track,
              predefinedOrderId: selectorCursorGenericOrders.predefinedOrderId,
              state: selectorCursorGenericOrders.state,
            },
          },
        });

  // El último `data` de la consulta que se copió a `allData`. Entre que llega
  // y el efecto lo copia hay un render en el que las filas siguen siendo las
  // de antes: también cuenta como cargando.
  const [syncedQueryData, setSyncedQueryData] = useState<unknown>();
  // Para qué `allData` están construidas las filas que se ven. Las filas no
  // salen de la consulta: las construye `selectAllOrders` DESPUÉS, con sus
  // consultas por fila, y mientras tanto se ven las de la página anterior.
  const [rowsBuiltFor, setRowsBuiltFor] = useState<unknown>();

  useEffect(() => {
    if (!loading && data) {
      setAllData(data);
      setSyncedQueryData(data);
    }
  }, [data, loading]);

  // Cargando de punta a punta (feedback usuario 28/10): la consulta Y las
  // filas construidas para lo último que llegó. Con solo `loading` el cargando
  // se apagaba con las filas viejas a la vista y parecía que el clic de página
  // no había hecho nada.
  const isBuildingRows =
    (!loading && data !== undefined && data !== syncedQueryData) ||
    rowsBuiltFor !== allData;
  const tableLoading = loading || isBuildingRows;

  const { refetch } =
    isCampaignTab
      ? useQuery(GET_CAMPAIGNS_MODAL, {
          skip: true,
          variables: {
            input: {
              cursor: '',
              take: Math.abs(pageSizeCampaigns),
              orderBy: 'desc',
              concessionId: effectiveCampaignConcessionId,
              fromDate: selectorCursorCampaign?.fromDate,
              toDate: selectorCursorCampaign?.toDate,
              name: selectorCursorCampaign.name,
              state: selectorCursorCampaign.state,
              teamIds: selectorCursorCampaign?.teamIds ?? [],
              track: selectorCursorCampaign?.track,
              predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
              pkInitKm: selectorCursorCampaign?.pkInitKm,
              pkInitMeter: selectorCursorCampaign?.pkInitMeter,
              pkEndKm: selectorCursorCampaign?.pkEndKm,
              pkEndMeter: selectorCursorCampaign?.pkEndMeter,
            },
          },
        })
      : useQuery(GET_GENERIC_ORDERS_MODAL, {
          skip: true,
          variables: {
            input: {
              take: Math.abs(pageSizeGenericsOrders),
              orderBy: 'desc',
              cursor: '',
              toDate: selectorCursorGenericOrders.toDate,
              fromDate: selectorCursorGenericOrders.fromDate,
              indexType: selectorCursorGenericOrders.indexType,
              indexExpedition: selectorCursorGenericOrders.indexExpedition,
              classification: selectorCursorGenericOrders.classification,
              capitolId: selectorCursorGenericOrders.capitolId,
              subCapitolId: selectorCursorGenericOrders.subCapitolId,
              concessionId: effectiveGenericConcessionId,
              pkInitKm: selectorCursorGenericOrders.pkInitKm,
              pkInitMeter: selectorCursorGenericOrders.pkInitMeter,
              pkEndKm: selectorCursorGenericOrders.pkEndKm,
              pkEndMeter: selectorCursorGenericOrders.pkEndMeter,
              teamIds: selectorCursorGenericOrders.teamIds ?? [],
              track: selectorCursorGenericOrders.track,
              predefinedOrderId: selectorCursorGenericOrders.predefinedOrderId,
              state: selectorCursorGenericOrders.state,
            },
          },
        });

  const ref = useRef<imperativeRefPagination>(null);

  // ═══ Modo RV (F2/F3): query de RVs destino ═══
  // Mismo field/policy que el listado (`surveillanceRoutes`) con cajón Apollo
  // propio vía @connection(key:"modal"). `take` SIEMPRE ±takeModalSurveillanceRoute:
  // el clamp preserva la dirección de paginación (take negativo = página
  // anterior, patrón fetchOrderID) y neutraliza el take=takeBase(25) que
  // handleSearch/onClearValue de la tabla compartida dispatchean en contexto
  // modal (useServerPageSize disabled → takePagination cae a takeBase).
  // Los estados de un RV cambian a menudo (open↔in-progress↔paused): antes
  // era `network-only` y CADA página, también volver a una ya vista, iba al
  // servidor. Revisión del 28-oct (el usuario aprueba la propuesta): al ABRIR
  // la ventana se borra lo guardado de esta lista —el merge de la primera
  // página conserva las siguientes de una apertura anterior, que serían
  // viejas— y, mientras sigue abierta, `cache-first` sirve las páginas ya
  // vistas. Borra el campo entero (`surveillanceRoutes`): el listado de
  // Recorridos volverá a pedir su primera página, que tampoco sobra.
  // El borrado va en el inicializador de un estado, ANTES de la query: en un
  // efecto, la query ya habría leído lo viejo en el primer render.
  useState(() => {
    if (showsRvTable) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'surveillanceRoutes' });
      client.cache.gc();
    }
    return null;
  });
  const rvTake =
    ((selectorCursorRv?.take ?? takeModalSurveillanceRoute) < 0 ? -1 : 1) *
    takeModalSurveillanceRoute;
  const {
    data: rvData,
    loading: rvLoading,
    error: rvError,
  } = useQuery(GET_SURVEILLANCE_ROUTES_MODAL, {
    // Fuera del modo RV la tabla de recorridos es la pestaña de convertir: se
    // pide al abrirla, no al abrir el modal.
    skip: !showsRvTable || (!isRvMode && activeTab !== 'rv'),
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        take: rvTake,
        cursor: selectorCursorRv?.cursor ?? '',
        orderBy: selectorCursorRv?.orderBy ?? 'desc',
        sortField: selectorCursorRv?.sortField,
        // Elegibilidad FORZADA (no editable por el usuario — filtro Estado
        // oculto): RVs operativos y, para gestión, también los pendientes de
        // validar (transferir tramo).
        // Para convertir (fuera del modo RV), ninguno en curso (M-8).
        states: isRvMode
          ? rvTransferTargetStates(roleActive)
          : rvTransferTargetStates(roleActive).filter(
              (state) => state !== OrderState.InProgress
            ),
        // Campo del cursor Redux = `routeName`; la variable backend es `name`
        // (mismo mapeo que useSurveillanceRoutesListing).
        name: selectorCursorRv?.routeName,
        concessionId: selectorCursorRv?.concessionId,
        teamIds: selectorCursorRv?.teamIds,
        fromDate: selectorCursorRv?.fromDate,
        toDate: selectorCursorRv?.toDate,
        // «Fecha final» (revisión del 27-oct): la tabla la pinta y ahora se
        // filtra por ella, como en «Vincular tramo».
        fromEndDate: selectorCursorRv?.fromEndDate,
        toEndDate: selectorCursorRv?.toEndDate,
        indexType: selectorCursorRv?.indexType,
        indexExpedition: selectorCursorRv?.indexExpedition,
        pkInitKm: selectorCursorRv?.pkInitKm,
        pkInitMeter: selectorCursorRv?.pkInitMeter,
        pkEndKm: selectorCursorRv?.pkEndKm,
        pkEndMeter: selectorCursorRv?.pkEndMeter,
      },
    },
  });

  // Lista CRUDA de la página actual (incluye el RV padre si cayó en ella):
  // la paginación cursor pivota sobre esta lista — el cursor backend RV es el
  // id de la ENTITY SurveillanceRoute — para no romper el encadenado de
  // páginas al excluir visualmente el padre.
  const rvRoutes: SurveillanceRoute[] = showsRvTable
    ? (rvData?.surveillanceRoutes?.surveillanceRoutes ?? [])
    : [];
  const rvPageInfo = rvData?.surveillanceRoutes?.pageInfo;
  const rvTotalCount = rvData?.surveillanceRoutes?.totalCount ?? 0;

  const makeTransfer = async (destination?: { id?: string; roid?: string }) => {
    // Guard de re-entrada SÍNCRONO: bajo red lenta (3G) un doble-clic disparaba
    // un 2º makeTransfer cuando el `selector` ya se había vaciado → mutación con
    // `newParentOrderId` undefined ("Field newParentOrderId of required type ID!
    // was not provided"). El ref bloquea al instante; el `disabled` del botón
    // (estado React) se propaga demasiado tarde para frenar el 2º clic.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      // FIX A6: `destination` es el snapshot capturado en `acceptData` EN EL
      // MOMENTO DEL ACCEPT — ya NO releemos `selector` (Redux) aquí. Offline,
      // entre aceptar y confirmar, una revalidación/deselect podía disparar
      // ROW_MODAL_FORM_DESELECTED sobre este namingPersistForm y vaciar
      // `getCurrentRowDataModal`, con lo que este guard saltaba pese a que el
      // usuario SÍ había elegido destino.
      if (!destination?.id) {
        throw new Error(
          'Destino de transferencia no disponible (newParentOrderId vacío)'
        );
      }
      // Idempotencia: UN clientOpId estable por intento de transferencia, horneado
      // en las variables ANTES de client.mutate para que RetryLink lo congele. Si
      // la red cae tras procesar en el servidor y RetryLink reintenta, el backend
      // hace replay de la Order-copia ya creada en vez de CLONAR la entidad otra
      // vez (2ª copia silenciosa). Un solo makeTransfer = un intento = un opId.
      const transferOpId = crypto.randomUUID();
      const cursorActions = getCursor(tableIds.VALIDATION_ACTIONS)(
        store.getState()
      );
      const cursorAccidents = getCursor(tableIds.ACCIDENTS)(store.getState());
      const cursorCommunication = getCursor(tableIds.WORKORDER_COMMUNICATIONS)(
        store.getState()
      );
      if (orderToTransfer.type === OrderType.Accident) {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: orderToTransfer.id,
          },
        });

        await client.mutate({
          mutation: TRANSFER_ACCIDENT,
          variables: {
            input: {
              newParentOrderId: destination.id,
              accidentId: order.accidentIDs[0],
              accidentOrderId: orderToTransfer.id,
              clientOpId: transferOpId,
            },
          },
          update(cache) {
            // Al transferir, la hija CAMBIA de padre y su copia nace nueva: los
            // listados dejan de ser válidos. Antes aquí solo se reseteaba la lista
            // de la propia hija (accidents), y se olvidaba `orders` — que es de donde
            // comen Multimedia y Órdenes de trabajo. Resultado: transferías y esas
            // tablas seguían sirviendo de caché una búsqueda anterior, sin pedir
            // nada al servidor (son cache-first). Se veía como «la búsqueda se
            // queda congelada y la fila nueva no aparece».
            //
            // `evictDerivedCaches` es el helper que ya usan validar, cerrar y
            // cambiar de estado: borra TODAS las variantes de los 7 listados
            // —Apollo tira la búsqueda cacheada sea cual sea su texto— y hace
            // gc(). El `cache.modify` de antes dejaba un cascarón vacío en vez de
            // quitar la entrada.
            evictDerivedCaches(cache);
          },
        });
        await client.query({
          query: FETCH_ACCIDENTS,
          variables: {
            input: {
              take: takeBase,
              cursor: '',
              concessionId: cursorAccidents.concessionId || autoConcessionId,
              weatherId: cursorAccidents.weatherId,
              mediumKnowledgeId: cursorAccidents.mediumKnowledgeId,
              orderBy: cursorAccidents.orderBy,
              toDate: cursorAccidents.toDate,
              fromDate: cursorAccidents.fromDate,
              indexType: cursorAccidents.indexType,
              indexExpedition: cursorAccidents.indexExpedition,
              classification: cursorAccidents.classification,
              // «Solo marcadas» (spec 2026-10-22 § 8.3): la primera página se
              // vuelve a pedir con el filtro que tenga puesto la tabla.
              onlyFlagged: cursorAccidents.onlyFlagged,
            },
          },
          fetchPolicy: 'network-only',
        });
      }

      if (orderToTransfer.type === OrderType.Action) {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: orderToTransfer.id,
          },
        });

        const { data: transferred } = await client.mutate({
          mutation: TRANSFER_ACTION,
          variables: {
            input: {
              newParentOrderId: destination.id,
              actionId: order.actionIDs[0],
              actionOrderId: orderToTransfer.id,
              clientOpId: transferOpId,
            },
          },
          update(cache) {
            // Al transferir, la hija CAMBIA de padre y su copia nace nueva: los
            // listados dejan de ser válidos. Antes aquí solo se reseteaba la lista
            // de la propia hija (actions), y se olvidaba `orders` — que es de donde
            // comen Multimedia y Órdenes de trabajo. Resultado: transferías y esas
            // tablas seguían sirviendo de caché una búsqueda anterior, sin pedir
            // nada al servidor (son cache-first). Se veía como «la búsqueda se
            // queda congelada y la fila nueva no aparece».
            //
            // `evictDerivedCaches` es el helper que ya usan validar, cerrar y
            // cambiar de estado: borra TODAS las variantes de los 7 listados
            // —Apollo tira la búsqueda cacheada sea cual sea su texto— y hace
            // gc(). El `cache.modify` de antes dejaba un cascarón vacío en vez de
            // quitar la entrada.
            evictDerivedCaches(cache);
          },
        });

        // Hecho: se dice, con el ROID de la copia (QA T5, 15-oct).
        if (transferred?.transferAction?.roid) {
          showSuccessToast(
            t('modals:confirmTransfer.success', {
              roid: transferred.transferAction.roid,
            })
          );
        }
        await client.query({
          query: FETCH_ACTIONS,
          variables: {
            input: {
              take: takeBase,
              cursor: '',
              concessionId: cursorActions.concessionId || autoConcessionId,
              capitolId: cursorActions.capitolId,
              subCapitolId: cursorActions.subCapitolId,
              orderBy: cursorActions.orderBy,
              toDate: cursorActions.toDate,
              fromDate: cursorActions.fromDate,
              indexType: cursorActions.indexType,
              indexExpedition: cursorActions.indexExpedition,
              classification: cursorActions.classification,
              state: cursorActions.state,
              // «Solo marcadas» (spec 2026-10-22 § 8.3): la primera página se
              // vuelve a pedir con el filtro que tenga puesto la tabla.
              onlyFlagged: cursorActions.onlyFlagged,
            },
          },
          fetchPolicy: 'network-only',
        });
      }
      if (orderToTransfer.type === OrderType.Communication) {
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: orderToTransfer.id,
          },
        });

        await client.mutate({
          mutation: TRANSFER_COMMUNICATION,
          variables: {
            input: {
              newParentOrderId: destination.id,
              communicationId: order.communicationIDs[0],
              communicationOrderId: orderToTransfer.id,
              clientOpId: transferOpId,
            },
          },
          update(cache) {
            // Al transferir, la hija CAMBIA de padre y su copia nace nueva: los
            // listados dejan de ser válidos. Antes aquí solo se reseteaba la lista
            // de la propia hija (communications), y se olvidaba `orders` — que es de donde
            // comen Multimedia y Órdenes de trabajo. Resultado: transferías y esas
            // tablas seguían sirviendo de caché una búsqueda anterior, sin pedir
            // nada al servidor (son cache-first). Se veía como «la búsqueda se
            // queda congelada y la fila nueva no aparece».
            //
            // `evictDerivedCaches` es el helper que ya usan validar, cerrar y
            // cambiar de estado: borra TODAS las variantes de los 7 listados
            // —Apollo tira la búsqueda cacheada sea cual sea su texto— y hace
            // gc(). El `cache.modify` de antes dejaba un cascarón vacío en vez de
            // quitar la entrada.
            evictDerivedCaches(cache);
          },
        });
        await client.query({
          query: FETCH_COMMUNICATIONS,
          variables: {
            input: {
              take: takeBase,
              cursor: '',
              orderBy: cursorCommunication.orderBy,
              roadId: cursorCommunication.roadId,
              concessionId:
                cursorCommunication.concessionId || autoConcessionId,
              interlocutorGroupId: cursorCommunication.interlocutorGroupId,
              fromDate: cursorCommunication.fromDate,
              toDate: cursorCommunication.toDate,
              indexType: cursorCommunication.indexType,
              indexExpedition: cursorCommunication.indexExpedition,
              classification: cursorCommunication?.classification,
              // «Solo marcadas» (spec 2026-10-22 § 8.3): la primera página se
              // vuelve a pedir con el filtro que tenga puesto la tabla.
              onlyFlagged: cursorCommunication.onlyFlagged,
            },
          },
          fetchPolicy: 'network-only',
        });
      }
      await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: destination.id,
        },
        fetchPolicy: 'network-only',
      });

      await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: currentParentOrder.id,
        },
        fetchPolicy: 'network-only',
      });

      await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: orderToTransfer.id,
        },
        fetchPolicy: 'network-only',
      });

      revalidatorFunction();
    } catch (error) {
      // Encolada offline por OfflineQueueLink → NO es un fallo: la mutation
      // (con su clientOpId congelado) se replay-eará al reconectar sin duplicar
      // y el banner ya muestra la operación pendiente. Cerrar limpio, sin toast
      // de error ni Sentry (antes salía "Ha ocurrido un error" — confuso).
      const e = error as {
        offlineQueued?: boolean;
        message?: string;
        networkError?: { offlineQueued?: boolean };
        graphQLErrors?: { message?: string }[];
      };
      const isOfflineQueued =
        e?.offlineQueued === true ||
        e?.networkError?.offlineQueued === true ||
        (e?.message ?? '').includes('Mutation queued for offline sync');
      if (isOfflineQueued) {
        console.log(
          '📴 Transferencia encolada offline — sincronizará al reconectar'
        );
        return;
      }
      Sentry.captureException(error);
      console.error('[TransferSubOrderModal] Error en transferencia:', error);
      // Surface del error: el modal ya no se queda colgado y el usuario se
      // entera. Preferimos el mensaje de negocio del backend (p.ej. "La orden
      // ya fue transferida o está anulada") si viene; si no, toast genérico i18n.
      const gqlMessage = e?.graphQLErrors?.[0]?.message;
      showErrorToast(gqlMessage || t('toasts:error.generic'));
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const acceptData = useCallback(async () => {
    // FIX A6: snapshot del destino EN EL MOMENTO DEL ACCEPT. Antes
    // `makeTransfer` releía `selector` (Redux) cuando el usuario pulsaba
    // "confirmar" en el CONFIRM_TRANSFER_MODAL — offline, entre aceptar y
    // confirmar, una revalidación/deselect podía disparar
    // ROW_MODAL_FORM_DESELECTED sobre este namingPersistForm y vaciar
    // `getCurrentRowDataModal`, con lo que el guard saltaba pese a que el
    // usuario SÍ había elegido destino. Ahora el destino se congela aquí y
    // viaja como argumento — el confirm ya no depende del Redux vivo.
    const destination = {
      id: selector?.data?.id,
      roid: selector?.data?.roid,
    };
    if (!destination.id) {
      showErrorToast(t('toasts:error.generic'));
      return;
    }
    // F4 (2026-07-21): aviso de desvínculo — si la básica de origen tiene un
    // tramo vinculado (`basicActionLinkedSegmentOrderIDs`, ya en el fragment
    // NEW_ORDER), el confirm añade la línea "se desvinculará al transferir"
    // (el backend suelta el vínculo del tramo origen dentro de la tx; la
    // básica aterriza en el RV destino SIN tramo y se re-vincula a mano).
    // cache-first: la card de la actuación acaba de pintarse desde esa misma
    // query — offline resuelve de caché, mismo raíl que makeTransfer.
    let showUnlinkSegmentWarning = false;
    let unlinkWarningRoid: string | undefined;
    if (isRvMode) {
      try {
        const {
          data: { order: sourceOrder },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id: orderToTransfer.id },
          fetchPolicy: 'cache-first',
        });
        showUnlinkSegmentWarning =
          (sourceOrder?.basicActionLinkedSegmentOrderIDs?.length ?? 0) > 0;
        unlinkWarningRoid = sourceOrder?.roid;
      } catch {
        // Sin caché ni red: se transfiere sin el aviso extra (no bloquear).
      }
    }
    dispatch(selectedRowModalAcceptData(namingPersistForm));
    dispatch(closeModal());
    dispatch(
      openModal(modalIds.CONFIRM_TRANSFER_MODAL, {
        onTransferOrder: async () => {
          await makeTransfer(destination);
          dispatch(closeModal());
          setIsLoading(false);
        },
        orderType: orderToTransfer?.type,
        // F4: en modo RV el tipo del NUEVO padre es explícitamente el RV.
        // Fuera de RV se conserva el quirk pre-existente (tipo del padre
        // ACTUAL, no del destino — anotado en la investigación, no se toca).
        newParentOrderType: isRvMode
          ? OrderType.SurveillanceRoute
          : currentParentOrder?.type,
        isLoading: isLoading,
        isSameOrder: destination.id === orderToTransfer?.orderParent,
        showUnlinkSegmentWarning,
        unlinkWarningRoid,
      })
    );
  }, [namingPersistForm, selector, isLoading, isRvMode]);

  useEffect(() => {
    // La selección RV tiene su propio sync (abajo).
    if (!showsOtTables || activeTab === 'rv') return;
    dispatch(
      selectedRowModalTable(namingPersistForm as string, campaignRowState)
    );
    // Una construcción vieja que acaba tarde no pisa las filas de otra más
    // nueva (otra página, otra búsqueda): al cambiar `allData` se cancela.
    let cancelled = false;
    const tasks = async () => {
      if (
        activeTab === 'generic' &&
        allData?.genericOrders?.genericOrders.length > 0
      ) {
        const genericOrders = await selectAllOrders(
          allData.genericOrders.genericOrders
        );
        if (cancelled) return;
        setGenericOrdersData(genericOrders);
      } else {
        setGenericOrdersData([]);
      }
      if (isCampaignTab && allData?.campaigns?.campaigns.length > 0) {
        const campaigns = await selectAllOrders(allData.campaigns.campaigns);
        if (cancelled) return;
        setCampaignData(campaigns);
      } else {
        setCampaignData([]);
      }
      setRowsBuiltFor(allData);
    };
    tasks().catch((err) => {
      Sentry.captureException(err);
      console.log(err);
      // Sin esto el cargando no se apagaría nunca. Quedan las filas de antes,
      // como hasta ahora.
      if (!cancelled) setRowsBuiltFor(allData);
    });
    if (error) {
      console.warn(error);
    }
    return () => {
      cancelled = true;
    };
  }, [allData, error, campaignRowState]);

  useEffect(() => {
    if (!showsOtTables || activeTab === 'rv') return;
    isCampaignTab
      ? dispatch(loadingData(tableIds.CAMPAIGN_MODAL, tableLoading))
      : dispatch(loadingData(tableIds.GENERIC_ORDER_MODAL, tableLoading));
  }, [tableLoading, isCampaignTab, activeTab]);

  // Modo RV: flag de loading de la tabla RV (query + resolución de Orders
  // gemelas) — el mismo mecanismo Loader-superpuesto de todas las tablas.
  useEffect(() => {
    if (!showsRvTable) return;
    dispatch(
      loadingData(
        tableIds.SURVEILLANCE_ROUTE_MODAL,
        rvLoading || isResolvingRvOrders
      )
    );
  }, [showsRvTable, rvLoading, isResolvingRvOrders]);

  // FIX (loader-loading-flag-leak): cleanup en unmount como defense-in-depth.
  // Si el usuario cierra el modal con la X mientras `loading` es true, sin
  // este cleanup el flag queda en true en Redux. En la práctica el siguiente
  // modal lo arregla solo (su useEffect dispatcha en el primer render con
  // el `loading` actual de useQuery, sobrescribiendo el flag fugado), y
  // GENERIC_ORDER_MODAL/CAMPAIGN_MODAL solo los leen las tablas internas
  // de los propios modales — pero mantenemos el cleanup para evitar la
  // ventana de flicker (cache hit instantáneo) y por coherencia con el
  // patrón "todo productor de loading flag debe limpiar".
  useEffect(() => {
    return () => {
      dispatch(loadingData(tableIds.CAMPAIGN_MODAL, false));
      dispatch(loadingData(tableIds.GENERIC_ORDER_MODAL, false));
      dispatch(loadingData(tableIds.SURVEILLANCE_ROUTE_MODAL, false));
    };
  }, []);

  const onReFetchData = useCallback(async () => {
    setCurrentServerPage(1);
    const { data } = await refetch();
    setAllData(data);
  }, [refetch]);

  const onReFetchPagination = useCallback(async () => {
    return data;
  }, [data]);

  const [
    dataCapitols,
    dataConcessions,
    dataSubCapitols,
    dataTeams,
    dataClassifications,
    dataPredefinedOrders,
  ] = [
    useQuery(FETCH_CAPITOLS),
    useQuery(GET_CONCESSIONS),
    useQuery(FETCH_SUB_CAPITOLS),
    useQuery(FETCH_TEAMS),
    useQuery(FETCH_CLASSIFICATIONS),
    useQuery(FETCH_PREDEFINED_ORDERS),
  ];
  const filters = {
    concessions: dataConcessions?.data?.concessions
      ? dataConcessions.data.concessions
      : [],
    teams: dataTeams?.data?.teams ? dataTeams.data.teams : [],
    predefinedOrders: dataPredefinedOrders?.data?.predefinedOrders
      ? dataPredefinedOrders.data.predefinedOrders
      : [],
  };
  const filtersDataOptions = {
    capitols: dataCapitols?.data?.capitols ? dataCapitols.data.capitols : [],
    concessions: dataConcessions?.data?.concessions
      ? dataConcessions.data.concessions
      : [],
    subCapitols: dataSubCapitols?.data?.subCapitols
      ? dataSubCapitols.data.subCapitols
      : [],
    teams: dataTeams?.data?.teams ? dataTeams.data.teams : [],
    classifications: dataClassifications?.data?.classifications
      ? dataClassifications.data.classifications
      : [],
    predefinedOrders: dataPredefinedOrders?.data?.predefinedOrders
      ? dataPredefinedOrders.data.predefinedOrders
      : [],
  };

  // ═══ Modo RV (F3): filas enriquecidas con la Order gemela ═══
  // El transfer necesita `newParentOrderId` = id de la ORDER del RV destino,
  // no el de la entity SurveillanceRoute (el adapter devuelve id=sr.id, que
  // NO vale como destino). Contrato espejo de selectAllOrders: fila.id =
  // Order gemela (viaja a makeTransfer sin tocarlo), genericOrCampaignOrderID
  // = entity id (cursor de paginación backend).
  useEffect(() => {
    if (!showsRvTable) return;
    let cancelled = false;
    const buildRows = async () => {
      if (rvRoutes.length === 0) {
        setRvRows([]);
        return;
      }
      setIsResolvingRvOrders(true);
      try {
        const ordersMap = await selectOrdersForSurveillanceRoutes(rvRoutes);
        if (cancelled) return;
        const rows = rvRoutes
          .map((sr) => {
            const twinOrder = ordersMap[sr.id];
            // Sin Order gemela resuelta no hay destino transferible — fuera
            // (mismo criterio de resiliencia por-fila que el selector).
            if (!twinOrder?.id) return undefined;
            return {
              ...adaptSurveillanceRouteToOrder(
                sr,
                filtersDataOptions,
                twinOrder
              ),
              id: twinOrder.id,
            };
          })
          .filter(
            (row): row is NonNullable<typeof row> =>
              Boolean(row) &&
              // "Transferir a OTRO RV": el RV padre actual no es destino —
              // se compara la Order gemela con el padre de la actuación.
              row?.id !== orderToTransfer?.orderParent
          );
        setRvRows(rows as unknown as dataTable[]);
      } catch (err) {
        Sentry.captureException(err);
        console.error('[TransferSubOrderModal] Error resolviendo RVs:', err);
      } finally {
        if (!cancelled) setIsResolvingRvOrders(false);
      }
    };
    buildRows().catch((err) => Sentry.captureException(err));
    return () => {
      cancelled = true;
    };
    // filtersDataOptions se recrea por render (lookups de catálogo, cacheados
    // por Apollo desde el boot) — deps sobre los datasets subyacentes.
  }, [
    showsRvTable,
    rvData,
    orderToTransfer?.orderParent,
    dataConcessions?.data,
    dataTeams?.data,
  ]);

  // Sync selección local → Redux (patrón tab Campañas): acceptData sigue
  // leyendo getCurrentRowDataModal(namingPersistForm) sin cambios.
  useEffect(() => {
    if (!showsRvTable || activeTab !== 'rv') return;
    const row = rvRows.find((r) => r.id === selectedRvOrderId);
    dispatch(
      selectedRowModalTable(
        namingPersistForm as string,
        row ? { id: row.id, roid: row.roid } : {}
      )
    );
  }, [showsRvTable, activeTab, selectedRvOrderId, rvRows]);

  // «Convertir» (W4): al cambiar de pestaña se cambia de destino — nada de lo
  // elegido en otra pestaña vale para esta.
  useEffect(() => {
    if (!canConvert) return;
    setSelectedRvOrderId('');
    setCampaignRowState({});
    setHasSelectedGenericOrderRow(false);
    setAnnulOnly(false);
    setSelectedSegmentId('');
    setSegmentPickError(null);
    setRouteSegments([]);
    dispatch(selectedRowModalTable(namingPersistForm as string, {}));
  }, [activeTab]);

  // Actuación de OT → básica: al marcar un recorrido, su tabla de Tramos. La
  // fila lleva la Order gemela (`id`) y la entidad (`genericOrCampaignOrderID`).
  const selectedRvRow = rvRows.find((r) => r.id === selectedRvOrderId);
  const selectedRouteEntityId = selectedRvRow?.genericOrCampaignOrderID as
    | string
    | undefined;
  useEffect(() => {
    setSelectedSegmentId('');
    setSegmentPickError(null);
    if (!isConvertTab || isRvMode || !selectedRouteEntityId) {
      setRouteSegments([]);
      return;
    }
    let cancelled = false;
    setIsLoadingSegments(true);
    client
      .query<{ routeSegments: RouteSegment[] }>({
        query: GET_ROUTE_SEGMENTS,
        variables: { surveillanceRouteId: selectedRouteEntityId },
        fetchPolicy: 'network-only',
      })
      .then(({ data: segmentsData }) => {
        if (!cancelled) setRouteSegments(segmentsData?.routeSegments ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setRouteSegments([]);
        showBackendErrorToast(err, t('toasts:error.generic'));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSegments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isConvertTab, isRvMode, selectedRouteEntityId]);

  const rvPageCount = Math.max(
    1,
    Math.ceil(rvTotalCount / takeModalSurveillanceRoute)
  );

  const nextPaginationRv = async () => {
    if (
      rvRoutes.length >= 1 &&
      rvPageInfo?.hasNextPage &&
      currentServerPage < rvPageCount
    ) {
      setCurrentServerPage((prev) => prev + 1);
      dispatch(
        setCursor(tableIds.SURVEILLANCE_ROUTE_MODAL as string, {
          ...selectorCursorRv,
          cursor: rvRoutes[rvRoutes.length - 1].id,
          take: takeModalSurveillanceRoute,
        })
      );
    }
  };

  const backPaginationRv = async () => {
    if (currentServerPage <= 2) {
      setCurrentServerPage(1);
      dispatch(
        setCursor(tableIds.SURVEILLANCE_ROUTE_MODAL as string, {
          ...selectorCursorRv,
          cursor: '',
          take: takeModalSurveillanceRoute,
        })
      );
      return;
    }
    if (rvRoutes.length >= 1 && rvPageInfo?.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      dispatch(
        setCursor(tableIds.SURVEILLANCE_ROUTE_MODAL as string, {
          ...selectorCursorRv,
          cursor: rvRoutes[0].id,
          take: -takeModalSurveillanceRoute,
        })
      );
    }
  };

  // handleSearch/onClearValue de la tabla ya dispatchean el cursor (página 1);
  // las variables del useQuery derivan de Redux y network-only baja datos
  // frescos al cambiar — aquí solo se realinea el contador local.
  const onReFetchDataRv = useCallback(async () => {
    setCurrentServerPage(1);
  }, []);

  const onReFetchPaginationRv = useCallback(async () => rvData, [rvData]);

  const rvTableData = {
    genericOrders: rvRows,
    filtersDataOptions,
    totalCount: rvTotalCount,
    pageInfo: rvPageInfo ?? { hasNextPage: false, hasPreviousPage: false },
    loading: rvLoading || isResolvingRvOrders,
    roleActive,
  };

  const pageInfo = isCampaignTab
    ? data?.campaigns.pageInfo
    : data?.genericOrders.pageInfo;

  const totalCount = isCampaignTab
    ? data?.campaigns.totalCount
    : data?.genericOrders.totalCount;

  // `loading` de las tablas = el de punta a punta (RelatedCampaignTable lo lee
  // de aquí; InputRadioDefaultTable, del flag de Redux de arriba).
  const combinedData = isCampaignTab
      ? {
          campaigns: campaignData,
          filters,
          totalCount,
          pageInfo,
          loading: tableLoading,
          roleActive,
        }
      : {
          genericOrdersData,
          filtersDataOptions,
          totalCount,
          pageInfo,
          loading: tableLoading,
          roleActive,
        };

  const fetchGenericOrderID = useCallback(
    async (id: string, take = pageSizeGenericsOrders) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorCursorGenericOrders?.indexType,
        indexExpedition: selectorCursorGenericOrders?.indexExpedition,
        concessionId: effectiveGenericConcessionId,
        fromDate: selectorCursorGenericOrders?.fromDate,
        toDate: selectorCursorGenericOrders?.toDate,
        orderBy: 'desc',
        classification: selectorCursorGenericOrders?.classification,
        capitolId: selectorCursorGenericOrders?.capitolId,
        subCapitolId: selectorCursorGenericOrders?.subCapitolId,
        pkInitKm: selectorCursorGenericOrders?.pkInitKm,
        pkInitMeter: selectorCursorGenericOrders?.pkInitMeter,
        pkEndKm: selectorCursorGenericOrders?.pkEndKm,
        pkEndMeter: selectorCursorGenericOrders?.pkEndMeter,
        teamIds: selectorCursorGenericOrders?.teamIds ?? [],
        track: selectorCursorGenericOrders?.track,
        predefinedOrderId: selectorCursorGenericOrders?.predefinedOrderId,
        state: selectorCursorGenericOrders?.state,
      };

      if (take > 0) {
        const lastGenericOrder =
          genericOrdersData[genericOrdersData.length - 1]
            .genericOrCampaignOrderID;
        if (lastGenericOrder) {
          dispatch(
            setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataSelector)
          );
        }
      } else {
        const firstGenericOrder = genericOrdersData[0].genericOrCampaignOrderID;

        if (firstGenericOrder) {
          dispatch(
            setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataSelector)
          );
        }
      }

      return dataSelector;
    },
    [selectorCursorGenericOrders, genericOrdersData]
  );

  const fetchCampaignID = useCallback(
    async (id: string, take = pageSizeCampaigns) => {
      const dataSelector = {
        take,
        cursor: id,
        concessionId: effectiveCampaignConcessionId,
        fromDate: selectorCursorCampaign?.fromDate,
        toDate: selectorCursorCampaign?.toDate,
        orderBy: 'desc',
        state: selectorCursorCampaign?.state,
        name: selectorCursorCampaign?.name,
        teamIds: selectorCursorCampaign?.teamIds ?? [],
        track: selectorCursorCampaign?.track,
        predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
        pkInitKm: selectorCursorCampaign?.pkInitKm,
        pkInitMeter: selectorCursorCampaign?.pkInitMeter,
        pkEndKm: selectorCursorCampaign?.pkEndKm,
        pkEndMeter: selectorCursorCampaign?.pkEndMeter,
      };

      if (take > 0) {
        const lastCampaign =
          campaignData[campaignData.length - 1].genericOrCampaignOrderID;
        if (lastCampaign) {
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataSelector));
        }
      } else {
        const firstCampaign = campaignData[0].genericOrCampaignOrderID;

        if (firstCampaign) {
          dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [selectorCursorCampaign, campaignData]
  );

  const nextPagination = async () => {
    const cursor = !isCampaignTab
      ? genericOrdersData[genericOrdersData.length - 1]
          ?.genericOrCampaignOrderID
      : campaignData[campaignData.length - 1]?.genericOrCampaignOrderID;
    const take = !isCampaignTab
      ? Math.abs(pageSizeGenericsOrders)
      : Math.abs(pageSizeCampaigns);
    console.log(
      `%c[TRANSFER-NEXT] page=${currentServerPage}, cursor=${cursor}, take=${take}, hasNext=${pageInfo?.hasNextPage}, dataLen=${!isCampaignTab ? genericOrdersData.length : campaignData.length}`,
      'color: green; font-weight: bold'
    );
    if (
      activeTab === 'generic' &&
      genericOrdersData.length >= 1 &&
      pageInfo.hasNextPage
    ) {
      setCurrentServerPage((prev) => prev + 1);
      await fetchGenericOrderID(
        genericOrdersData[genericOrdersData.length - 1]
          .genericOrCampaignOrderID as string
      );
      onReFetchPagination();
    }
    if (isCampaignTab && campaignData.length >= 1 && pageInfo.hasNextPage) {
      setCurrentServerPage((prev) => prev + 1);
      await fetchCampaignID(
        campaignData[campaignData.length - 1]
          .genericOrCampaignOrderID as string,
        pageSizeCampaigns
      );
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    // If going to page 1, reset cursor to get correct first items
    if (currentServerPage <= 2) {
      const dataCursor = isCampaignTab
          ? {
              take: pageSizeCampaigns,
              cursor: '',
              concessionId: effectiveCampaignConcessionId,
              fromDate: selectorCursorCampaign?.fromDate,
              toDate: selectorCursorCampaign?.toDate,
              orderBy: 'desc',
              name: selectorCursorCampaign?.name,
              state: selectorCursorCampaign?.state,
              teamIds: selectorCursorCampaign?.teamIds ?? [],
              track: selectorCursorCampaign?.track,
              predefinedOrderId: selectorCursorCampaign?.predefinedOrderId,
              pkInitKm: selectorCursorCampaign?.pkInitKm,
              pkInitMeter: selectorCursorCampaign?.pkInitMeter,
              pkEndKm: selectorCursorCampaign?.pkEndKm,
              pkEndMeter: selectorCursorCampaign?.pkEndMeter,
            }
          : {
              take: pageSizeGenericsOrders,
              cursor: '',
              indexType: selectorCursorGenericOrders?.indexType,
              indexExpedition: selectorCursorGenericOrders?.indexExpedition,
              concessionId: effectiveGenericConcessionId,
              fromDate: selectorCursorGenericOrders?.fromDate,
              toDate: selectorCursorGenericOrders?.toDate,
              classification: selectorCursorGenericOrders?.classification,
              orderBy: 'desc',
              capitolId: selectorCursorGenericOrders?.capitolId,
              subCapitolId: selectorCursorGenericOrders?.subCapitolId,
              state: selectorCursorGenericOrders?.state,
              pkInitKm: selectorCursorGenericOrders?.pkInitKm,
              pkInitMeter: selectorCursorGenericOrders?.pkInitMeter,
              pkEndKm: selectorCursorGenericOrders?.pkEndKm,
              pkEndMeter: selectorCursorGenericOrders?.pkEndMeter,
              teamIds: selectorCursorGenericOrders?.teamIds ?? [],
              track: selectorCursorGenericOrders?.track,
              predefinedOrderId: selectorCursorGenericOrders?.predefinedOrderId,
            };
      setCurrentServerPage(1);
      isCampaignTab
        ? dispatch(setCursor(tableIds.CAMPAIGN_MODAL as string, dataCursor))
        : dispatch(
            setCursor(tableIds.GENERIC_ORDER_MODAL as string, dataCursor)
          );
      onReFetchPagination();
      return;
    }
    const cursor = !isCampaignTab
      ? genericOrdersData[0]?.genericOrCampaignOrderID
      : campaignData[0]?.genericOrCampaignOrderID;
    const rawTake = !isCampaignTab ? pageSizeGenericsOrders : pageSizeCampaigns;
    const actualTake = !isCampaignTab
      ? -Math.abs(pageSizeGenericsOrders)
      : -Math.abs(pageSizeCampaigns);
    console.log(
      `%c[TRANSFER-BACK] page=${currentServerPage}, cursor=${cursor}, rawPageSize=${rawTake}, actualTake=${actualTake}, hasPrev=${pageInfo?.hasPreviousPage}, dataLen=${!isCampaignTab ? genericOrdersData.length : campaignData.length}`,
      'color: red; font-weight: bold'
    );
    if (
      activeTab === 'generic' &&
      genericOrdersData.length >= 1 &&
      pageInfo.hasPreviousPage
    ) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchGenericOrderID(
        genericOrdersData[0].genericOrCampaignOrderID as string,
        -Math.abs(pageSizeGenericsOrders)
      );
      onReFetchPagination();
    }
    if (
      isCampaignTab &&
      campaignData.length >= 1 &&
      pageInfo.hasPreviousPage
    ) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCampaignID(
        campaignData[0].genericOrCampaignOrderID as string,
        -Math.abs(pageSizeCampaigns)
      );
      onReFetchPagination();
    }
  };

  // ═══ «Convertir actuación» (W4, 15-oct) ═══
  // Los tramos en el orden de la tabla del recorrido: el Nº de la frase es el
  // de la fila que se ha marcado.
  const sortedRouteSegments = useMemo(
    () =>
      [...routeSegments].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      ),
    [routeSegments]
  );
  const selectedSegmentNumber =
    sortedRouteSegments.findIndex((s) => s.id === selectedSegmentId) + 1;
  // La columna «Carretera» con nombres, como «Registrar parada»: las vías del
  // recorrido más las de los tramos (QA T5: salía «-»).
  const selectedRoute = rvRoutes.find((sr) => sr.id === selectedRouteEntityId);
  const segmentRoadIds = useMemo(
    () => roadIdsInvolved(selectedRoute?.roadIDs, routeSegments),
    [selectedRoute?.roadIDs, routeSegments]
  );
  const segmentRoadNames = useRoadNamesById(segmentRoadIds);
  // Lo que el servidor rechazaría, dicho al tocar el tramo y sin marcarlo (QA
  // T5): un tramo abierto o anulado no lleva básica; uno resuelto por una
  // básica viva ya tiene la suya; y uno que avisa a OTRA OT no se suelta en
  // silencio. El que avisa a la OT de origen sí vale: ese aviso es el que se
  // suelta al pasarla. El servidor sigue teniendo la última palabra.
  const segmentRejection = (segment: RouteSegment): string | null => {
    if (!segment.endDate || segment.state === OrderState.Annulled) {
      return 'ORDERS_CONVERT_SEGMENT_INVALID';
    }
    if (liveLinkedBasicActionOrder(segment.linkedBasicActionOrder)) {
      return 'ORDERS_CONVERT_SEGMENT_ALREADY_RESOLVED';
    }
    const linkedOrderId = segment.linkedExternalOrder?.id;
    if (linkedOrderId && linkedOrderId !== currentParentOrder?.id) {
      return 'ORDERS_CONVERT_SEGMENT_LINKED_OTHER_ORDER';
    }
    return null;
  };
  const onPickSegment = (segment: RouteSegment) => {
    const rejection = segmentRejection(segment);
    setSegmentPickError(rejection);
    setSelectedSegmentId(rejection ? '' : segment.id);
  };

  // El destino elegido en la pestaña de convertir: una OT o campaña (la fila
  // marcada viaja por Redux, como al transferir) o un recorrido (estado local).
  // Las filas de OT y campañas llevan el ROID partido por puntos (la tabla lo
  // pinta por piezas): para la frase se vuelve a juntar.
  const roidText = (roid: unknown): string | undefined =>
    Array.isArray(roid) ? roid.join('.') : (roid as string | undefined);
  const convertTarget: { id?: string; roid?: string } = !isConvertTab
    ? {}
    : isRvMode
      ? { id: selector?.data?.id, roid: roidText(selector?.data?.roid) }
      : {
          id: selectedRvRow?.id as string | undefined,
          roid: roidText(selectedRvRow?.roid),
        };
  const hasConvertTarget = isRvMode
    ? isCampaignTab
      ? Boolean(campaignRowState?.id)
      : hasSelectedGenericOrderRow
    : Boolean(selectedRvOrderId);
  // Una actuación con trabajos externos o fauna no puede ser básica: se dice
  // al abrir la pestaña de recorridos, no al confirmar (QA T5).
  const blockedByExternalOrFauna =
    !isRvMode && orderToTransfer?.hasExternalOrFauna === true;
  const convertReady =
    isConvertTab &&
    !blockedByExternalOrFauna &&
    hasConvertTarget &&
    Boolean(convertTarget.id) &&
    (isRvMode || Boolean(selectedSegmentId));

  // Debajo, en una frase, lo que va a pasar.
  const convertSummary = (() => {
    if (!isConvertTab || !hasConvertTarget || !convertTarget.id) return null;
    const roid = orderToTransfer?.roid ?? '';
    const target = convertTarget.roid ?? '';
    if (isRvMode) {
      const segment = orderToTransfer?.linkedSegmentRoid;
      if (annulOnly) {
        return segment
          ? t('modals:convertAction.summaryAnnulOnlySegment', {
              roid,
              target,
              segment,
            })
          : t('modals:convertAction.summaryAnnulOnly', { roid, target });
      }
      return segment
        ? t('modals:convertAction.summaryToOrderSegment', {
            roid,
            target,
            segment,
          })
        : t('modals:convertAction.summaryToOrder', { roid, target });
    }
    if (!selectedSegmentId) return null;
    return t('modals:convertAction.summaryToBasic', {
      roid,
      target,
      number: selectedSegmentNumber,
    });
  })();

  /**
   * Convierte y cierra. Gesto de oficina: va FUERA de la cola offline
   * (`skipOfflineQueue`) — sin red falla a la vista. Si el servidor lo
   * rechaza, el modal sigue abierto con el motivo en un toast.
   */
  const makeConvert = async () => {
    if (isSubmittingRef.current || !convertReady || !convertTarget.id) return;
    isSubmittingRef.current = true;
    setIsLoading(true);
    // ROID de la copia (null si solo se anula la básica), para el aviso de éxito.
    let copyRoid: string | null | undefined;
    try {
      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: { id: orderToTransfer.id },
      });
      const segment = routeSegments.find((s) => s.id === selectedSegmentId);
      const targetId = convertTarget.id;
      const { data: converted } = await client.mutate({
        mutation: CONVERT_ACTION,
        variables: {
          input: {
            mode: convertModeFor({ sourceIsBasic: isRvMode, annulOnly }),
            actionId: order.actionIDs[0],
            actionOrderId: orderToTransfer.id,
            targetOrderId: targetId,
            ...(isRvMode ? {} : { targetSegmentId: selectedSegmentId }),
            clientOpId: convertOpId,
          },
        },
        context: { skipOfflineQueue: true },
        update(cache) {
          // Como al transferir un tramo: cambian las horas de los turnos, las
          // causas y los vínculos de los tramos, y las dos fichas.
          evictAfterSegmentEdit(cache, [
            orderToTransfer.id,
            targetId,
            currentParentOrder.id,
            ...(segment?.orderIDs ?? []),
            ...(order.basicActionLinkedSegmentOrderIDs ?? []),
          ]);
          cache.evict({ fieldName: 'routeSegments' });
          cache.gc();
        },
      });
      copyRoid = converted?.convertAction?.copyRoid;
    } catch (error) {
      // Sin red la petición ni sale (va con `skipOfflineQueue`): decirlo así,
      // no «Ha ocurrido un error», que manda a buscar un fallo que no hay (QA
      // T5). Misma regla de «es red» que la cola offline.
      if (isLegacyNetworkFailure(error)) {
        showErrorToast(t('common:offline.noConnection'));
        return;
      }
      showBackendErrorToast(error, t('toasts:error.generic'));
      return;
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
    // Convertida: el modal se cierra YA (revisión W4, M-3). Las recargas de
    // después no pueden convertirlo en un fallo: si una cae, la pantalla se
    // revalida igual y lo que falte se pide al volver a ella.
    dispatch(closeModal());
    // Y se dice, con el ROID nuevo (QA T5): antes se cerraba sin más.
    showSuccessToast(
      copyRoid
        ? t('modals:convertAction.success', { roid: copyRoid })
        : t('modals:convertAction.successAnnulOnly', {
            roid: orderToTransfer?.roid ?? '',
          })
    );
    try {
      for (const id of [
        convertTarget.id,
        currentParentOrder.id,
        orderToTransfer.id,
      ]) {
        await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id },
          fetchPolicy: 'network-only',
        });
      }
    } catch (error) {
      console.warn('[TransferSubOrderModal] Recarga tras convertir:', error);
    }
    revalidatorFunction();
  };

  const convertButtonText = isRvMode
    ? annulOnly
      ? t('modals:convertAction.confirmAnnulOnly')
      : t('modals:convertAction.confirmToOrder')
    : t('modals:convertAction.confirmToBasic');

  // En las tabs seria bueno poner un loader para que el usuario vea que esta cargando
  const genericContent = (
    <Container {...props}>
      <InputRadioDefaultTable
        data={combinedData as DataTypesGenericOrderModal}
        onReFetchData={onReFetchData}
        onRefetchPagination={onReFetchPagination}
        namingPersistForm={namingPersistForm}
        ref={ref}
        titleText={t('pages:workOrders.filterBy')}
        tableId={tableIds.GENERIC_ORDER_MODAL}
        setIsSelectedRow={setHasSelectedGenericOrderRow}
        onDoubleTapAccept={isConvertTab ? undefined : acceptData}
        disabledRowStates={['end', 'annulled']}
      />
    </Container>
  );
  const campaignContent = (
    <Container {...props}>
      <RelatedCampaignTable
        titleText={t('pages:campaigns.filterBy')}
        data={combinedData as dataTypes}
        setRowState={setCampaignRowState}
        onReFetchData={onReFetchData}
        onReFetchPagination={onReFetchPagination}
        tableId={tableIds.CAMPAIGN_MODAL}
        showButtonsPagination={false}
        setPageSize={setPageSizeCampaigns}
        onDoubleTapAccept={isConvertTab ? undefined : acceptData}
        namingPersistForm={namingPersistForm}
        disabledRowStates={['end', 'annulled']}
      />
    </Container>
  );

  // ═══ Modo RV (F3): UNA tabla de RVs operativos ═══
  // Reuso de InputRadioTagTable TAL CUAL (precedente obligatorio
  // RelateSegmentModal — 2 rechazos del usuario a tablas hermanas nuevas).
  // Sin pestañas el único título es el "Filtrar Recorridos por:" de la propia
  // tabla (misma composición que RelateSegmentModal); con pestañas (gestión,
  // W4) es la pestaña «Recorridos de vigilancia».
  const rvTable = (
    <>
      <InputRadioTagTable
        titleText={t('pages:surveillance.filterBy')}
        data={rvTableData as unknown as InputRadioTagTableProps['data']}
        setRowState={setSelectedRvOrderId}
        onReFetchData={onReFetchDataRv}
        onReFetchPagination={onReFetchPaginationRv}
        tableId={tableIds.SURVEILLANCE_ROUTE_MODAL}
        hiddenColumnIds={RV_TRANSFER_MODAL_HIDDEN_COLUMN_IDS}
        hiddenFilterIds={RV_TRANSFER_MODAL_HIDDEN_FILTER_IDS}
        showEndDateFilter
        showRouteNameColumn
        showButtonsPagination={false}
        onRowDoubleTap={(row) => {
          // Solo selecciona (sin auto-accept): acceptData lee la selección
          // de Redux y el sync local→Redux es un efecto — aceptar en el
          // mismo gesto leería el estado anterior. Definir el handler es
          // además obligatorio: sin él la tabla compartida navegaría a su
          // default `/work-orders/:id`.
          if (row.original.id) setSelectedRvOrderId(row.original.id);
        }}
      />
      {!rvLoading && !isResolvingRvOrders && rvError && (
        <EmptyMessage>{t('common:messages.error')}</EmptyMessage>
      )}
      {!rvLoading && !isResolvingRvOrders && !rvError && rvRows.length === 0 && (
        <EmptyMessage>{t('pages:surveillance.noRoutes')}</EmptyMessage>
      )}
    </>
  );

  const rvPagination = (
    <PaginationButtonsStyled
      pageCount={rvPageCount}
      currentPage={currentServerPage}
      nextPage={() =>
        rvPageInfo?.hasNextPage &&
        currentServerPage < rvPageCount &&
        nextPaginationRv()
      }
      previousPage={() => currentServerPage > 1 && backPaginationRv()}
      totalCount={rvTotalCount}
    />
  );

  if (tabKinds.length === 1) {
    return (
      <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
        <Container {...props}>
          {rvTable}
          <ButtonContainer>
            <CloseButton
              text={t('common:buttons.back')}
              variant="secondary"
              onClick={onClickClose}
            />
            {rvPagination}
            <MainButtonStyled
              disabled={!selectedRvOrderId}
              text={t('modals:confirmTransfer.transferButton')}
              onClick={acceptData}
            />
          </ButtonContainer>
        </Container>
      </ModalContainer>
    );
  }

  const tabContent: Record<TabKind, { label: string; content: JSX.Element }> =
    {
      generic: { label: t('common:tabs.workOrders'), content: genericContent },
      campaign: { label: t('common:tabs.campaigns'), content: campaignContent },
      rv: {
        label: t('common:tabs.surveillanceRoutes'),
        content: <Container {...props}>{rvTable}</Container>,
      },
    };
  const dataTabs = tabKinds.map((kind) => tabContent[kind]);

  const otPageSize = Math.abs(
    isCampaignTab ? pageSizeCampaigns : pageSizeGenericsOrders
  );
  const otPageCount = Math.max(1, Math.ceil((totalCount ?? 0) / otPageSize));

  // Convertir (W4): debajo de la tabla, las opciones del doble registro o la
  // tabla de Tramos del recorrido elegido, y la frase de lo que va a pasar.
  const convertBlock = isConvertTab ? (
    <BelowTable data-testid="convert-action-block">
      {blockedByExternalOrFauna && (
        <Notice role="alert" data-testid="convert-action-external-warning">
          <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
          <span>{t('errors:backend.ORDERS_CONVERT_EXTERNAL_OR_FAUNA')}</span>
        </Notice>
      )}
      {isRvMode && hasConvertTarget && (
        <>
          <ModeRow $disabled={false}>
            <input
              type="radio"
              name="convert-action-option"
              aria-label={t('modals:convertAction.optionMove')}
              checked={!annulOnly}
              onChange={() => setAnnulOnly(false)}
            />
            <OptionText size="S" component="span">
              {t('modals:convertAction.optionMove')}
            </OptionText>
          </ModeRow>
          <ModeRow $disabled={false}>
            <input
              type="radio"
              name="convert-action-option"
              aria-label={t('modals:convertAction.optionAnnulOnly')}
              checked={annulOnly}
              onChange={() => setAnnulOnly(true)}
            />
            <OptionText size="S" component="span">
              {t('modals:convertAction.optionAnnulOnly')}
            </OptionText>
          </ModeRow>
        </>
      )}
      {!isRvMode && selectedRouteEntityId && (
        <>
          <Text size="S" variant="semiBold">
            {t('modals:convertAction.pickSegment')}
          </Text>
          {!isLoadingSegments && (
            <SegmentList
              segments={routeSegments}
              selectedSegmentId={selectedSegmentId}
              onSegmentClick={onPickSegment}
              roadNamesById={segmentRoadNames}
            />
          )}
          {segmentPickError && (
            <InlineError size="S" data-testid="convert-action-segment-error">
              {t(`errors:backend.${segmentPickError}`)}
            </InlineError>
          )}
        </>
      )}
      {convertSummary && (
        <Text size="S" variant="semiBold" data-testid="convert-action-summary">
          {convertSummary}
        </Text>
      )}
      {/* Revisión W4 (M-7): una básica sin fin pasa con el fin del hueco de
          su tramo si el servidor lo sabe; si no, sin horas. Se avisa. */}
      {isRvMode && !annulOnly && convertSummary && !orderToTransfer?.endDate && (
        <Notice role="alert" data-testid="convert-action-no-end-warning">
          <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
          <span>{t('modals:convertAction.noEndDate')}</span>
        </Notice>
      )}
      {/* Revisión W4 (M-6): lo que NO pasa a la básica, dicho antes. */}
      {!isRvMode && convertSummary && (
        <Notice role="alert" data-testid="convert-action-crew-warning">
          <NoticeIcon aria-hidden="true">⚠</NoticeIcon>
          <span>{t('modals:convertAction.toBasicLeavesCrew')}</span>
        </Notice>
      )}
    </BelowTable>
  ) : null;

  const mainButton = isConvertTab ? (
    <MainButtonStyled
      disabled={!convertReady || isLoading}
      text={convertButtonText}
      onClick={() => void makeConvert()}
      data-testid="convert-action-confirm"
    />
  ) : activeTab === 'rv' ? (
    <MainButtonStyled
      disabled={!selectedRvOrderId}
      text={t('modals:confirmTransfer.transferButton')}
      onClick={acceptData}
    />
  ) : (
    <MainButtonStyled
      disabled={
        isCampaignTab ? !campaignRowState?.id : !hasSelectedGenericOrderRow
      }
      text={t('modals:relatesCommunication.relateButton')}
      onClick={acceptData}
    />
  );

  return (
    <ModalContainer onClickClose={onClickClose} fill={theme.colors.greyBoard}>
      <Tabs
        defaultIndex={0}
        data={dataTabs}
        typeTab={tabsType.TRANSFER_SUBORDER}
        setSelectedIndexTab={setTabIndex}
      />
      {convertBlock}
      <ButtonContainer>
        <CloseButton
          text={t('common:buttons.back')}
          variant="secondary"
          onClick={onClickClose}
        />
        {activeTab === 'rv' ? (
          rvPagination
        ) : (
          <PaginationButtonsStyled
            pageCount={otPageCount}
            currentPage={currentServerPage}
            nextPage={() =>
              pageInfo?.hasNextPage &&
              currentServerPage < otPageCount &&
              nextPagination()
            }
            previousPage={() => currentServerPage > 1 && backPagination()}
            totalCount={totalCount}
            // Mientras carga, las filas a la vista son las de antes y el cursor
            // de la página siguiente sale de su última fila: pediría otra vez
            // la misma página.
            isDisabled={tableLoading}
          />
        )}
        {mainButton}
      </ButtonContainer>
    </ModalContainer>
  );
};
